import React, { useState } from 'react';
import { useNavigate } from "react-router-dom";
import config from '../../config/config';

export default function AddIncome() {
  const userId = localStorage.userId;
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    itemName: "",
    personName: "",
    rate: "",
    qty: "",
    total: "0"
  });

  const validateForm = () => {
    const newErrors = {};
    if (!formData.itemName.trim()) newErrors.itemName = "Item name is required";
    if (!formData.personName.trim()) newErrors.personName = "Buyer name is required";
    if (!formData.rate || Number(formData.rate) <= 0) newErrors.rate = "Enter a valid rate";
    if (!formData.qty || Number(formData.qty) <= 0) newErrors.qty = "Enter a valid quantity";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "rate" || name === "qty") {
        const r = name === "rate" ? value : prev.rate;
        const q = name === "qty" ? value : prev.qty;
        updated.total = (Number(r) * Number(q)).toString();
      }
      return updated;
    });
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${config.baseUrl}/api/otherInc/addOthInc`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          item_name: formData.itemName,
          person_name: formData.personName,
          rate: formData.rate,
          qty: formData.qty,
          total_amount: formData.total,
          userId
        })
      });
      if (response.ok) {
        navigate('/other-income/view');
      } else {
        const errorData = await response.json();
        alert(errorData.message || "Failed to add income");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("A server error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const inpCls = (field) =>
    `w-full px-3 py-2.5 text-sm rounded-lg border outline-none transition-all
    focus:border-slate-400 bg-gray-50
    ${errors[field]
      ? 'border-red-400 bg-red-50 focus:border-red-400'
      : 'border-gray-200'
    }`;

  const labelCls = "block text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5";

  return (
    <div className="py-2 px-2 max-w-2xl mx-auto">

      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Add other income</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">

        {/* Item Details Card */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-indigo-800 border-b border-slate-100">
            <span className="text-[12px] font-bold uppercase tracking-wider text-white">Item details</span>
          </div>
          <div className="p-4 grid grid-cols-2 gap-3">

            <div>
              <label className={labelCls}>Item type</label>
              <input
                type="text"
                name="itemName"
                value={formData.itemName}
                onChange={handleInputChange}
                className={inpCls("itemName")}
                placeholder="e.g. Skin, Waste"
              />
              {errors.itemName && <p className="text-red-500 text-xs mt-1">{errors.itemName}</p>}
            </div>

            <div>
              <label className={labelCls}>Buyer / person name</label>
              <input
                type="text"
                name="personName"
                value={formData.personName}
                onChange={handleInputChange}
                className={inpCls("personName")}
                placeholder="Who paid?"
              />
              {errors.personName && <p className="text-red-500 text-xs mt-1">{errors.personName}</p>}
            </div>

          </div>
        </div>

        {/* Pricing Card */}
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="px-4 py-2.5 bg-indigo-800 border-b border-slate-100">
            <span className="text-[12px] font-bold uppercase tracking-wider text-white">Pricing</span>
          </div>
          <div className="p-4 space-y-3">

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Rate (per unit)</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none">Rs</span>
                  <input
                    type="number"
                    name="rate"
                    value={formData.rate}
                    onChange={handleInputChange}
                    className={`${inpCls("rate")} pl-7`}
                    placeholder="0"
                    min="0"
                  />
                </div>
                {errors.rate && <p className="text-red-500 text-xs mt-1">{errors.rate}</p>}
              </div>

              <div>
                <label className={labelCls}>Quantity</label>
                <input
                  type="number"
                  name="qty"
                  value={formData.qty}
                  onChange={handleInputChange}
                  className={inpCls("qty")}
                  placeholder="0"
                  min="0"
                />
                {errors.qty && <p className="text-red-500 text-xs mt-1">{errors.qty}</p>}
              </div>
            </div>

            {/* Total Strip */}
            <div className="flex justify-between items-center px-4 py-3 bg-indigo-50 border border-green-100 rounded-lg">
              <span className="text-xs font-bold text-indigo-800">Total income</span>
              <span className="text-lg font-black text-indigo-700 font-mono">
                Rs. {Number(formData.total).toLocaleString()}
              </span>
            </div>

          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="flex-1 py-2.5 text-sm bg-white font-bold text-slate-500 border border-slate-200 rounded-lg hover:bg-slate-50 transition-all"
          >
            Discard
          </button>
          <button
            type="submit"
            disabled={loading}
            className="flex-[2] py-2.5 text-sm font-bold text-white bg-indigo-800 rounded-lg hover:bg-indigo-700 active:scale-95 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-green-200/30 border-t-green-100 rounded-full animate-spin"></div>
                Processing...
              </>
            ) : 'Save income'}
          </button>
        </div>

      </form>
    </div>
  );
}